import React, { useMemo, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { components, palette } from '../../utils/designTokens';
import { getTableCellColor, getTableRowStyle, tableGridHeaderClassName, tablePanelTitleClassName, TableSortHeader } from '../TablePrimitives';
import SignalPill from '../SignalPill';

const GRID_TEMPLATE = 'minmax(0, 1fr) 64px minmax(160px, 1fr) minmax(200px, 1.2fr) 92px';

const toCount = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getNegativeCount = (workflow) => toCount(workflow?.negativeCount ?? workflow?.negative_count);
const getTotalCount = (workflow) => toCount(workflow?.total ?? workflow?.total_traces ?? workflow?.totalTraces);
const getPendingCount = (workflow) => toCount(workflow?.pendingCount ?? workflow?.pending_count);
const getSignalCount = (workflow) => {
  const explicit = workflow?.signalCount ?? workflow?.signal_count;
  if (explicit !== undefined && explicit !== null) return toCount(explicit);

  const topSignals = Array.isArray(workflow?.topSignals) ? workflow.topSignals : [];
  return topSignals.reduce((sum, signal) => sum + toCount(signal?.count), 0);
};

const SORT_ACCESSORS = {
  total: getTotalCount,
  negativeCount: getNegativeCount,
  signalCount: getSignalCount,
};

const WorkflowDistributionPanel = ({
  workflows = [],
  loading = false,
  error = null,
  onWorkflowClick,
  onSignalClick,
  onOutcomeClick,
  onWorkflowEvalClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [sortKey, setSortKey] = useState('signalCount');
  const [sortDirection, setSortDirection] = useState('desc');
  const defaultLimit = 6;

  const handleSort = (nextKey) => {
    if (nextKey === sortKey) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
      return;
    }
    setSortKey(nextKey);
    setSortDirection('desc');
  };

  const sortedWorkflows = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey] || getTotalCount;
    return [...(Array.isArray(workflows) ? workflows : [])]
      .sort((a, b) => {
        const diff = accessor(a) - accessor(b);
        if (diff !== 0) return sortDirection === 'asc' ? diff : -diff;
        return `${a.workflowName || ''}`.localeCompare(`${b.workflowName || ''}`);
      });
  }, [workflows, sortKey, sortDirection]);

  const maxTotal = useMemo(() => (
    sortedWorkflows.reduce((max, workflow) => Math.max(max, getTotalCount(workflow)), 0)
  ), [sortedWorkflows]);

  const visibleWorkflows = expanded ? sortedWorkflows : sortedWorkflows.slice(0, defaultLimit);

  return (
    <div
      className={`${components.card.bg} ${components.card.rounded} p-4`}
      style={{ border: `1px solid ${components.card.borderColor}` }}
    >
      <div className="mb-3">
        <h3 className={tablePanelTitleClassName} style={{ color: palette.text.supporting }}>
          Top Business Tasks
        </h3>
      </div>

      {loading && (
        <div className="text-sm py-4" style={{ color: palette.text.muted }}>
          Loading business tasks...
        </div>
      )}

      {!loading && error && (
        <div className="text-sm py-4" style={{ color: palette.status.danger }}>
          {error}
        </div>
      )}

      {!loading && !error && sortedWorkflows.length === 0 && (
        <div className="text-sm py-4" style={{ color: palette.text.muted }}>
          No business task data available for this range.
        </div>
      )}

      {!loading && !error && sortedWorkflows.length > 0 && (
        <div className="flex flex-col">
          <div
            className={tableGridHeaderClassName}
            style={{ gridTemplateColumns: GRID_TEMPLATE, columnGap: '14px' }}
          >
            <span>Name</span>
            <TableSortHeader
              active={sortKey === 'total'}
              direction={sortDirection}
              label="Attempts"
              align="right"
              onClick={() => handleSort('total')}
            />
            <div className="pl-1">
              <TableSortHeader
                active={sortKey === 'negativeCount'}
                direction={sortDirection}
                label="Outcome"
                onClick={() => handleSort('negativeCount')}
              />
            </div>
            <div className="pl-1">
              <TableSortHeader
                active={sortKey === 'signalCount'}
                direction={sortDirection}
                label="Signals"
                onClick={() => handleSort('signalCount')}
              />
            </div>
            <span className="text-right">Evals</span>
          </div>
          {visibleWorkflows.map((workflow, index) => {
            const workflowName = workflow.workflowName || workflow.canonical_name || 'Unknown business task';
            const failed = getNegativeCount(workflow);
            const pending = getPendingCount(workflow);
            const total = getTotalCount(workflow);
            const barWidth = maxTotal > 0 ? Math.max(4, (total / maxTotal) * 100) : 0;
            const negativeRatio = total > 0 ? (failed / total) : 0;
            const rowKey = workflow.workflowId || workflowName;
            return (
              <div
                key={rowKey}
                role="button"
                tabIndex={0}
                className={`text-left px-2 py-2 transition-colors ${components.table.row.border}`}
                style={getTableRowStyle(index, {
                  isHovered: hoveredRowKey === rowKey,
                  interactive: true,
                })}
                onMouseEnter={() => setHoveredRowKey(rowKey)}
                onMouseLeave={() => setHoveredRowKey(null)}
                onClick={() => onWorkflowClick?.(workflow)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onWorkflowClick?.(workflow);
                  }
                }}
                title={`Open traces for business task ${workflowName}`}
              >
                <div
                  className="grid items-start"
                  style={{ gridTemplateColumns: GRID_TEMPLATE, columnGap: '14px' }}
                >
                  <span className="text-sm truncate" style={{ color: getTableCellColor('entity') }} title={workflowName}>
                    {workflowName}
                  </span>
                  <span
                    className="text-sm text-right tabular-nums"
                    style={{ color: getTableCellColor('metric'), fontVariantNumeric: 'tabular-nums' }}
                  >
                    {workflow.total}
                  </span>

                  <div className="flex flex-col gap-1 pl-1">
                    <div
                      className="h-2 rounded-sm overflow-hidden"
                      style={{ backgroundColor: palette.neutrals.surfaceMid }}
                    >
                      <div
                        className="h-full rounded-sm flex"
                        style={{ width: `${barWidth}%` }}
                      >
                        {negativeRatio > 0 && (
                          <div
                            style={{
                              width: `${negativeRatio * 100}%`,
                              backgroundColor: palette.status.danger,
                              opacity: 0.7,
                            }}
                            className="h-full"
                          />
                        )}
                        <div
                          style={{
                            flex: 1,
                            backgroundColor: palette.accents.primary.base,
                            opacity: 0.5,
                          }}
                          className="h-full"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                      {failed > 0 ? (
                        <button
                          type="button"
                          className="p-0 border-0 bg-transparent hover:opacity-80 transition-opacity"
                          style={{ color: palette.status.danger }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOutcomeClick?.(workflow, 'failure');
                          }}
                          title={`Open not completed traces for ${workflowName}`}
                        >
                          {failed} not completed
                        </button>
                      ) : (
                        <span style={{ color: palette.text.muted }}>
                          0 not completed
                        </span>
                      )}
                      {pending > 0 && (
                        <button
                          type="button"
                          className="p-0 border-0 bg-transparent hover:opacity-80 transition-opacity"
                          style={{ color: palette.text.muted }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOutcomeClick?.(workflow, 'pending');
                          }}
                          title={`Open pending traces for ${workflowName}`}
                        >
                          {pending} pending
                        </button>
                      )}
                    </div>
                  </div>

                  {workflow.topSignals?.length > 0 ? (
                    <div className="flex flex-wrap gap-1 pl-1">
                      {workflow.topSignals.map((signal) => (
                        <button
                          key={`${workflowName}-${signal.key}`}
                          type="button"
                          className="p-0 border-0 bg-transparent rounded-full hover:opacity-80 transition-opacity"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSignalClick?.(workflow, signal);
                          }}
                          title={`Open traces for ${signal.label} in ${workflowName}`}
                        >
                          <SignalPill
                            label={`${signal.label} (${signal.count})`}
                            variant={signal.polarity === 'positive' ? 'info' : 'danger'}
                          />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[10px] pl-1" style={{ color: palette.text.muted }}>
                      No detected signals
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="inline-flex h-7 items-center gap-1 rounded border px-2 text-[11px] font-medium transition-colors hover:opacity-90"
                      style={{
                        color: palette.accents.primary.base,
                        borderColor: palette.neutrals.borderSubtle,
                        backgroundColor: palette.neutrals.surfaceSubtle,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onWorkflowEvalClick?.(workflow);
                      }}
                      title={`Show eval suggestions for ${workflowName}`}
                    >
                      <ClipboardCheck size={13} />
                      <span>Evals</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {sortedWorkflows.length > defaultLimit && (
            <button
              type="button"
              className="mt-1 py-1.5 rounded border text-xs font-medium transition-colors hover:opacity-90"
              style={{
                borderColor: palette.neutrals.borderSubtle,
                color: palette.accents.primary.base,
                backgroundColor: palette.neutrals.surfaceSubtle,
              }}
              onClick={() => setExpanded(prev => !prev)}
            >
              {expanded ? `Show Top ${defaultLimit}` : `Show All (${sortedWorkflows.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowDistributionPanel;
